import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useResizeObserver } from 'hooks/use-resize-observer';
import { TextPreset } from 'services/story.service';
import { useAppSelector } from 'store/store-hooks';
import { loadFontOnce, waitForFontToRender } from './_load-font.service';
import { __BASE_SIZE } from './_title-credits.params';

import styles from './_title-credits.module.scss';

interface Props {
  text: string;
  preset: TextPreset;
  onReady?: () => void;
}

export const __TextLayer = ({ text, preset, onReady }: Props) => {
  const fonts = useAppSelector((state) => state.app__data.fonts);

  const [position, _setPosition] = useState({ left: 0, bottom: 0, scale: 1 });
  const [fontFamily, setfontFamily] = useState('');

  const prevEl = useRef<HTMLDivElement>(null);
  const textEl = useRef<HTMLDivElement>(null);

  const setPosition = () => {
    const container = prevEl.current;
    if (!text || !container || !textEl.current) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const widthRatio = containerWidth / __BASE_SIZE.width;
    const heightRatio = containerHeight / __BASE_SIZE.height;
    const scale = Math.min(widthRatio, heightRatio);

    const positionX = preset.coordinates.x * containerWidth;
    const positionY = preset.coordinates.y * containerHeight;

    const elementWidth = textEl.current.clientWidth * scale;
    const elementHeight = textEl.current.clientHeight * scale;

    _setPosition({
      left: positionX - elementWidth / 2,
      bottom: positionY - elementHeight / 2,
      scale,
    });
  };

  const textFormatted = useMemo(() => {
    return text.split('\n').map((line, index) => <div key={index}>{line}</div>);
  }, [text]);

  const textShadow = useMemo(() => {
    return generateTextOutlineShadow(preset.borderWidth, preset.borderColor);
  }, [preset.borderWidth, preset.borderColor]);

  useResizeObserver(textEl.current, setPosition);
  useResizeObserver(prevEl.current, setPosition);
  useLayoutEffect(() => {
    setPosition();
  }, [preset.coordinates]);

  useEffect(() => {
    if (!fonts || preset.font === null) return;
    const fontData = fonts.find((el) => el.value === preset.font);
    if (!fontData) return;
    (async () => {
      await loadFontOnce(fontData.name, fontData.path);
      setfontFamily(fontData.name);
      await waitForFontToRender(textEl.current!);
      setPosition();
      if (onReady) onReady();
    })();
  }, [preset.font]);

  return (
    <div ref={prevEl} className={styles.TextLayer}>
      <div
        ref={textEl}
        className={styles.TextLayer__el}
        style={{
          color: preset.fillColor,
          fontSize: `${preset.fontSize}px`,
          fontFamily: `${fontFamily}, ${fontFamily} EXPORT`,
          textShadow: `${textShadow}, 0px 0px 20px ${preset.shadowColor}`,
          transform: `scale(${position.scale})`,
          bottom: `${position.bottom}px`,
          left: `${position.left}px`,
        }}
      >
        {textFormatted}
      </div>
    </div>
  );
};

/* H E L P E R S */

const generateTextOutlineShadow = (radius: number, color: string): string => {
  const shadows: string[] = [];
  for (let y = -radius; y <= radius; y++) {
    for (let x = -radius; x <= radius; x++) {
      if (x * x + y * y <= radius * radius) {
        shadows.push(`${x}px ${y}px 0 ${color}`);
      }
    }
  }
  return shadows.join(', ');
};
